import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { DEMO_PATIENTS, DEMO_CASES, DEMO_PHASES, DEMO_PHOTOS } from '../../contexts/DemoContext';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { getPatients } from '../../services/patientService';
import { getCasesForPatient } from '../../services/caseService';
import { getPhasePhotos } from '../../services/photoService';
import { getCategoryById } from '../../constants/dawsonCategories';

const IS_DEMO = process.env.REACT_APP_DEMO_MODE === 'true';

const PhotoGallery = () => {
  const { userProfile } = useAuth();
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [expandedPatient, setExpandedPatient] = useState(null);

  useEffect(() => {
    loadAllPhotos();
  }, [userProfile]);

  const loadAllPhotos = async () => {
    if (!userProfile?.practiceId) return;
    try {
      setLoading(true);
      setError('');

      if (IS_DEMO) {
        const allPhotos = [];
        for (const patient of DEMO_PATIENTS) {
          const patientCases = DEMO_CASES[patient.id] || [];
          for (const caseItem of patientCases) {
            const phases = DEMO_PHASES[caseItem.id] || [];
            for (const phase of phases) {
              const phasePhotos = DEMO_PHOTOS[phase.id] || [];
              phasePhotos.forEach(photo => {
                allPhotos.push({
                  ...photo,
                  patientName: `${patient.firstName} ${patient.lastName}`,
                  patientId: patient.id,
                  caseName: caseItem.name,
                  phaseType: phase.phaseType
                });
              });
            }
          }
        }
        allPhotos.sort((a, b) => new Date(b.uploadedAt) - new Date(a.uploadedAt));
        setPhotos(allPhotos);
        setLoading(false);
        return;
      }

      const patients = await getPatients(userProfile.practiceId, userProfile.id);
      const allPhotos = [];

      for (const patient of patients) {
        try {
          const cases = await getCasesForPatient(
            userProfile.practiceId, userProfile.id, patient.id
          );
          for (const caseItem of cases) {
            const phasesRef = collection(
              db, 'practices', userProfile.practiceId,
              'patients', patient.id,
              'cases', caseItem.id,
              'phases'
            );
            const phasesSnap = await getDocs(phasesRef);
            for (const phaseDoc of phasesSnap.docs) {
              try {
                const phasePhotos = await getPhasePhotos(
                  userProfile.practiceId, patient.id, caseItem.id, phaseDoc.id
                );
                phasePhotos.forEach(photo => {
                  allPhotos.push({
                    ...photo,
                    patientName: `${patient.firstName} ${patient.lastName}`,
                    patientId: patient.id,
                    caseName: caseItem.name,
                    phaseType: phaseDoc.data().phaseType
                  });
                });
              } catch (err) {
                // Skip phases with errors
              }
            }
          }
        } catch (err) {
          // Skip patients with errors
        }
      }

      allPhotos.sort((a, b) => {
        const dateA = a.uploadedAt?.toDate?.() || new Date(a.uploadedAt);
        const dateB = b.uploadedAt?.toDate?.() || new Date(b.uploadedAt);
        return dateB - dateA;
      });
      setPhotos(allPhotos);
    } catch (err) {
      console.error('Error loading photos:', err);
      setError('Failed to load photo gallery');
    } finally {
      setLoading(false);
    }
  };

  const patientGroups = useMemo(() => {
    const groups = {};
    photos.forEach(photo => {
      if (!groups[photo.patientId]) {
        groups[photo.patientId] = {
          patientId: photo.patientId,
          patientName: photo.patientName,
          photos: []
        };
      }
      groups[photo.patientId].photos.push(photo);
    });
    return Object.values(groups).sort((a, b) => a.patientName.localeCompare(b.patientName));
  }, [photos]);

  const formatDate = (date) => {
    if (!date) return '';
    if (date.toDate) return date.toDate().toLocaleDateString();
    return new Date(date).toLocaleDateString();
  };

  const togglePatient = (patientId) => {
    setExpandedPatient(expandedPatient === patientId ? null : patientId);
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-sm text-gray-500">Loading photo gallery...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto space-y-4 sm:space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Photo Gallery</h1>
        <p className="text-sm text-gray-500 mt-1">
          {photos.length} photo{photos.length !== 1 ? 's' : ''} across {patientGroups.length} patient{patientGroups.length !== 1 ? 's' : ''}
        </p>
      </div>

      {error && (
        <div className="rounded-md bg-red-50 p-4">
          <p className="text-sm text-red-800">{error}</p>
        </div>
      )}

      {patientGroups.length > 0 ? (
        <div className="space-y-4">
          {patientGroups.map((group) => {
            const isExpanded = expandedPatient === group.patientId;
            const previewPhotos = group.photos.slice(0, 4);

            return (
              <div key={group.patientId} className="bg-white shadow rounded-lg overflow-hidden">
                {/* Patient folder header */}
                <button
                  onClick={() => togglePatient(group.patientId)}
                  className="w-full flex items-center gap-4 p-4 hover:bg-gray-50 transition-colors text-left"
                >
                  <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                    <svg className="w-5 h-5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
                    </svg>
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-sm font-semibold text-gray-900">{group.patientName}</h3>
                    <p className="text-xs text-gray-500">{group.photos.length} photo{group.photos.length !== 1 ? 's' : ''}</p>
                  </div>

                  {/* Thumbnail previews when collapsed */}
                  {!isExpanded && (
                    <div className="hidden sm:flex items-center gap-1.5">
                      {previewPhotos.map((photo) => (
                        <div key={photo.id} className="w-10 h-10 rounded overflow-hidden bg-gray-100 flex-shrink-0">
                          <img src={photo.downloadURL} alt="" className="w-full h-full object-cover" loading="lazy" />
                        </div>
                      ))}
                      {group.photos.length > 4 && (
                        <span className="text-xs text-gray-400 ml-1">+{group.photos.length - 4}</span>
                      )}
                    </div>
                  )}

                  <svg
                    className={`w-5 h-5 text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                    fill="none" viewBox="0 0 24 24" stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {/* Expanded photo grid */}
                {isExpanded && (
                  <div className="border-t border-gray-100 p-4">
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2 sm:gap-3">
                      {group.photos.map((photo) => {
                        const category = getCategoryById(photo.category);
                        return (
                          <div
                            key={photo.id}
                            className="group relative rounded-lg overflow-hidden border border-gray-200 bg-white hover:shadow-md transition-shadow cursor-pointer"
                            onClick={() => setSelectedPhoto(photo)}
                          >
                            <div className="aspect-square">
                              <img
                                src={photo.downloadURL}
                                alt={category?.name || photo.category}
                                className="w-full h-full object-cover"
                                loading="lazy"
                              />
                            </div>
                            <div className="p-1.5">
                              <p className="text-xs font-medium text-gray-800 truncate">
                                {category?.name || photo.category}
                              </p>
                              <p className="text-xs text-gray-400 truncate">{photo.caseName}</p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    <div className="mt-3 text-right">
                      <Link
                        to={`/patients/${group.patientId}`}
                        className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                      >
                        View Patient Record &rarr;
                      </Link>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <div className="bg-white shadow rounded-lg p-12 text-center">
          <svg className="mx-auto h-16 w-16 text-gray-300 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
          <p className="text-gray-500 font-medium">No photos yet</p>
          <p className="text-sm text-gray-400 mt-1">
            Upload photos through a patient's case to start building your gallery
          </p>
          <Link
            to="/patients"
            className="inline-flex items-center mt-4 px-4 py-2 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700"
          >
            Go to Patients
          </Link>
        </div>
      )}

      {/* Photo Modal */}
      {selectedPhoto && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-90 flex items-center justify-center p-4">
          <button
            onClick={() => setSelectedPhoto(null)}
            className="absolute top-4 right-4 text-white hover:text-gray-300"
          >
            <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          <div className="max-w-4xl w-full">
            <img
              src={selectedPhoto.downloadURL}
              alt={getCategoryById(selectedPhoto.category)?.name || selectedPhoto.category}
              className="max-h-[75vh] mx-auto object-contain rounded-lg"
            />
            <div className="mt-4 text-center text-white">
              <p className="font-medium">
                {getCategoryById(selectedPhoto.category)?.name || selectedPhoto.category}
              </p>
              <p className="text-sm text-gray-300">
                {selectedPhoto.patientName} &mdash; {selectedPhoto.caseName}
              </p>
              <Link
                to={`/patients/${selectedPhoto.patientId}`}
                className="inline-block mt-2 text-sm text-blue-400 hover:text-blue-300"
              >
                View Patient
              </Link>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PhotoGallery;
